import React from 'react';
import { useIntl } from 'react-intl';
import { ReleaseKitSummary } from '../../../../../../redux/api/fymAPI.types';
import { TypedMemo } from '../../../../../common/types';
import { SMLButton } from '../../../../../common/views/SMLButton/SMLButton';
import { releaseThemeMessages } from '../../../../../music24/promote/themes/releaseThemeMessages';
import { SongStatusPill } from '../SongPageChips';
import { songPageMessages } from '../songPageMessages';
import { Panel, PanelBody, PanelHead } from '../../../../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song page; each part uses its own slice of it */
import styles from '../SongPage.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SongKitCardProps {
  kit: ReleaseKitSummary | undefined;
  readOnly: boolean;
  onReleases: () => void;
  onStudio: () => void;
}

/**
 * The Release Kit in short (design e1): the theme, the ready assets, the work that runs, and the
 * way to Releases and the Studio. The kit itself stays in Promote → Releases.
 */
const SongKitCardComponent: React.FC<SongKitCardProps> = ({ kit, readOnly, onReleases, onStudio }) => {
  const intl = useIntl();
  const themeId = kit?.look?.themeId;

  return (
    <Panel data-testid="song-page-kit">
      <PanelHead
        title={intl.formatMessage(songPageMessages.kitTitle)}
        right={
          themeId !== undefined ? (
            <SongStatusPill tone="run" label={intl.formatMessage(releaseThemeMessages[themeId].name)} />
          ) : undefined
        }
      />
      <PanelBody className={styles.cardBody}>
        {kit === undefined || kit.totalCount === 0 ? (
          <span className={styles.help}>{intl.formatMessage(songPageMessages.kitNothing)}</span>
        ) : (
          <div className="d-flex justify-content-between align-items-center gap-2">
            <span>
              {intl.formatMessage(songPageMessages.kitReady, { ready: kit.readyCount, total: kit.totalCount })}
            </span>
            {kit.workingCount > 0 && (
              <SongStatusPill
                tone="run"
                label={intl.formatMessage(songPageMessages.kitWorking, { count: kit.workingCount })}
              />
            )}
          </div>
        )}
        <div className="d-flex flex-wrap gap-2">
          <SMLButton size="sm" color="primary" omitCap onClick={onReleases}>
            {intl.formatMessage(songPageMessages.openInReleases)}
          </SMLButton>
          {!readOnly && (
            <SMLButton size="sm" outline omitCap onClick={onStudio}>
              {intl.formatMessage(songPageMessages.makeInStudio)}
            </SMLButton>
          )}
        </div>
      </PanelBody>
    </Panel>
  );
};

export const SongKitCard = TypedMemo(SongKitCardComponent);
